import {
    Form,
    FormField,
    FormUploadButton,
    FormFieldConditions,
    FormSection,
    FormType,
} from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';
import {
    DropdownData,
    InputType,
    RepeatableSubField,
} from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import {
    ReactiveFormChangedField,
    ReactiveFormValue,
} from 'src/app/common/components/reactive-form-builder/interfaces/reactive-form-change.interface';
import { list } from 'src/app/common/classes/request-builder';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { ClienteModel } from 'src/app/models/cliente.model';
import { CargoModel } from 'src/app/models/trabajadores/cargos.model';
import { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import { MantenimientoModel } from 'src/app/models/trabajadores/mantenimiento.model';
import { Compradores } from 'src/app/models/ventas/compradores.model';
import { environment } from 'src/environments/environment';
import { FIRST_CHAR_UNION } from 'src/app/common/components/reactive-form-builder/utils/spanish-id.keyfilter';

const CARNET_SEPARATOR = ';';
const DEFAULT_ROWS = 2;
const AVAFITOGEST_ROWS = 3;

export class MantenimientoListsDefinition {
    public maquinaria: DropdownData<MaquinariaModel> = list<MaquinariaModel>(['...', null]);

    public cargos: DropdownData<CargoModel> = list<CargoModel>(['...', null]);

    public propiedad: DropdownData<string> = list<string>(['...', null], ['Propios', 'Propios'], ['Cliente', 'Cliente']);

    public cuadrillas: DropdownData<MantenimientoModel> = list<MantenimientoModel>(['...', null]);

    public comercios: DropdownData<Compradores> = list<Compradores>(['...', null]);

    public clientes: DropdownData<ClienteModel> = list<ClienteModel>(['...', null]);

    public tipoCarnet: DropdownData<string> = list<string>(
        ['...', null],
        'BÁSICO',
        'CUALIFICADO',
        'FUMIGADOR',
        'PILOTO',
        'ASESOR',
    );

    public tipoCarnetPyf: DropdownData<string> = list<string>(
        ['...', null],
        'BÁSICO',
        'CUALIFICADO',
        'FUMIGADOR',
        'PILOTO',
    );
}

export class MantenimientoFormDefinition {
    private readonly formType!: FormType;

    public readonly lists: MantenimientoListsDefinition = new MantenimientoListsDefinition();

    public readonly appName = environment.appName;

    public readonly activateREA = environment.features.activateREA;

    public readonly hasClients = environment.features.hasClients;

    public readonly showTrabajadoresCargos = environment.features.showTrabajadoresCargos;

    public readonly showAplicadoresActivo = environment.features.showAplicadoresActivo;

    public readonly showTrabajadoresFirma = environment.features.showTrabajadoresFirma;

    public readonly filterAplicadoresByClient = environment.features.filterAplicadoresByClient;

    public readonly showTrabajadoresUserYPass = environment.features.showTrabajadoresUserYPass;

    public readonly trabajadoresAsUsuarios = environment.features.trabajadoresAsUsuarios;

    public readonly showTrabajadoresClienteAsignado = environment.features.showTrabajadoresClienteAsignado;

    public readonly clienteName = environment.features.clienteName;

    public readonly showTrabajadoresCuadrillas = environment.features.showTrabajadoresCuadrillas;

    public readonly showTrabajadoresNumSS = environment.features.showTrabajadoresNumSS;

    public readonly showTrabajadoresMaquinaria = environment.features.showTrabajadoresMaquinaria;

    public readonly showRopoRomaRegepa = environment.features.showRopoRomaRegepa;

    public readonly showFotoTrajador = environment.features.showFotoTrajador;

    public readonly titleActivo = environment.features.titleActivoMaqTra;

    public readonly textoClienteAsignado = this.clienteName + ' asignad' + (this.clienteName.slice(-1) === 'a' ? 'a' : 'o');

    public model: ReactiveFormValue<MantenimientoModel> = {} as ReactiveFormValue<MantenimientoModel>;

    public lastChangedFieldName: ReactiveFormChangedField<MantenimientoModel> = '';

    public formFields!: Form<MantenimientoModel>;

    public fieldNames: FormField<MantenimientoModel>[] = [];

    public fieldLabels: string[] = [];

    /**
     * Creates the worker maintenance form definition shared by edit forms and list modals.
     *
     * The definition centralizes worker fields, dropdown lists, repeated card rows,
     * upload controls and hidden fields so all maintenance modes use the same source of truth.
     *
     * @constructor
     * @public
     *
     * @param type - Request type used to define the form behaviour.
     * @param model - Optional worker model used as initial form data.
     * @param table - Optional table configuration used by table-based form modes.
     *
     * @returns A new worker maintenance form definition instance.
     */
    constructor(
        type: FormType,
        model: Partial<MantenimientoModel> | null = {},
        table: (string[] | string[][])[] | null = null,
    ) {
        this.formType = +type;
        this.model = {
            ...this.model,
            ...(model ?? {}),
        } as ReactiveFormValue<MantenimientoModel>;
        this.normalizeModel();
        this.formFields = this.buildForm(table);
    }// constructor();

    /**
     * Updates the stored model and rebuilds visible section metadata.
     *
     * @public
     *
     * @param model - Partial worker model received from the reactive form.
     * @param lastChangedFieldName - Last field changed by the user or by the builder.
     *
     * @returns Nothing.
     */
    public updateModel(
        model: ReactiveFormValue<MantenimientoModel>,
        lastChangedFieldName: ReactiveFormChangedField<MantenimientoModel>,
    ): void {
        this.model = {
            ...this.model,
            ...model,
        } as ReactiveFormValue<MantenimientoModel>;
        this.lastChangedFieldName = lastChangedFieldName;
        this.normalizeModel();
        this.refreshSections();
    }// updateModel();

    /**
     * Rebuilds section visibility and field metadata without replacing dropdown lists.
     *
     * @public
     *
     * @returns Nothing.
     */
    public refreshSections(): void {
        this.fieldNames = [];
        this.fieldLabels = [];
        this.formFields = {
            ...this.formFields,
            sections: this.getFormSections(),
            uploadButtons: this.getUploadButtons(),
        };
    }// refreshSections();

    /**
     * Builds and returns the main worker fields.
     *
     * @public
     *
     * @returns The worker fields sorted by display order.
     */
    public getWorkerFields(): FormField<MantenimientoModel>[] {
        const workerFields: FormField<MantenimientoModel>[] = [
            this.textField('nombre', 'Nombre', 1, true, 3, { isRequired: true }),
            this.textField('primer_apellido', 'Primer Apellido', 2),
            this.textField('segundo_apellido', 'Segundo Apellido', 3),
            {
                name: 'nif',
                vinculatedFields: undefined,
                label: 'NIF',
                input: { type: InputType.TEXT, isEditable: this.isEditable(), maxLength: 9 },
                width: 3,
                conditions: this.conditions(),
                keyFilter: FIRST_CHAR_UNION,
                order: 4,
            },
            this.textField(this.getPasswordFichajeFieldName(), 'Password', 5, this.isPasswordFichajeVisible()),
            this.dropdownField('cuadrilla', 'Cuadrilla', 6, this.lists.cuadrillas, this.showTrabajadoresCuadrillas),
            this.textField('numss', 'Nº\u00a0Afiliación', 7, this.showTrabajadoresNumSS),
            {
                name: 'id_maquina',
                vinculatedFields: undefined,
                label: 'Máquina',
                input: {
                    type: this.isEditable() ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT,
                    isEditable: this.isEditable(),
                    data: this.lists.maquinaria,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (option: { value: unknown }): boolean => this.filterMachineryOption(option.value),
                    },
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.showTrabajadoresMaquinaria }),
                order: 8,
            },
            this.dropdownField('id_cargo', 'Cargo', 9, this.lists.cargos, this.showTrabajadoresCargos),
            this.textField('email', 'Email', 10, this.appName === 'fotoagricultura'),
            {
                name: 'id_comercio',
                vinculatedFields: undefined,
                label: this.appName === 'sirga' ? 'Proveedor' : 'Empresa Servicios Agrícolas',
                input: {
                    type: this.isEditable() ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    isEditable: this.isEditable(),
                    data: this.lists.comercios,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (option: { value: unknown }): boolean => this.filterCommerceOption(option.value),
                    },
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 3,
                conditions: this.conditions({
                    isVisible: environment.features.showVentas || this.appName === 'covidai' || this.appName === 'sirga',
                }),
                order: 11,
            },
            this.textField('username', 'Usuario', 12, this.showTrabajadoresUserYPass),
            this.passwordField('password', 'Contraseña', 13, this.showTrabajadoresUserYPass, { isRequired: true }),
            this.passwordField('password_repeat', 'Repetir contraseña', 14, this.showTrabajadoresUserYPass),
            {
                name: 'acceso_plataforma',
                vinculatedFields: undefined,
                label: 'Acceso a Plataforma',
                input: { type: InputType.SWITCH, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'innovia' || this.appName === 'tomega' }),
                order: 15,
            },
            {
                name: 'observaciones',
                vinculatedFields: undefined,
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: this.isEditable(),
                    textAreaConfiguration: { rows: 8, hasVoiceTranscription: false, canTakePhoto: false, photoField: undefined },
                },
                width: 3,
                conditions: this.conditions(),
                order: 16,
            },
            {
                name: 'id_cliente_asignado',
                vinculatedFields: undefined,
                label: this.textoClienteAsignado,
                input: {
                    type: this.isEditable() ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT,
                    isEditable: this.isEditable(),
                    data: this.lists.clientes,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 3,
                conditions: this.conditions({ isRequired: true, isVisible: this.showTrabajadoresClienteAsignado }),
                order: 17,
            },
            this.dropdownField('propiedad', 'Propiedad', 18, this.lists.propiedad, this.appName === 'agropecuariaramirogruas', false),
            {
                name: 'es_almacen',
                vinculatedFields: undefined,
                label: 'Almacén',
                input: { type: InputType.SWITCH, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'naturfrut' }),
                order: 19,
            },
            {
                name: 'activo',
                vinculatedFields: undefined,
                label: this.getActiveLabel(),
                input: { type: InputType.SWITCH, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.showAplicadoresActivo }),
                order: 20,
            },
        ];

        this.appendVisibleFields(workerFields);
        return workerFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getWorkerFields();

    /**
     * Builds and returns the repeated card section field.
     *
     * @public
     *
     * @returns The repeatable card field used to edit one or more ROPO cards.
     */
    public getCarnetFields(): FormField<MantenimientoModel>[] {
        const carnetFields: FormField<MantenimientoModel>[] = [
            {
                name: 'carnetsGroup',
                vinculatedFields: ['nif'],
                label: '',
                input: {
                    type: InputType.REPEATABLE_GROUP,
                    isEditable: this.isEditable(),
                    repeatableGroupConfiguration: {
                        rows: this.getCarnetRowsLimit(),
                        addButtonLabel: 'Añadir otro carnet',
                        bindings: this.getRepeatableBindings(),
                        serialization: { separator: CARNET_SEPARATOR },
                        subFields: this.getRepeatableSubFields(),
                    },
                },
                width: 12,
                conditions: this.conditions(),
                order: 1,
            },
        ];

        this.appendVisibleFields(carnetFields);
        return carnetFields;
    }// getCarnetFields();

    /**
     * Builds hidden fields required by submit and repeated-card serialization.
     *
     * @public
     *
     * @returns The hidden fields sorted by display order.
     */
    public getHiddenFields(): FormField<MantenimientoModel>[] {
        const hiddenFieldNames = [
            'id',
            'id_cliente',
            'id_usuario',
            'tipo_usuario',
            'adjunto',
            'tipo_carnet',
            'ropo',
            'fecha_exp',
            'fecha_cad',
            'signature_img',
        ];

        const hiddenFields = hiddenFieldNames.map((fieldName, index) => this.hiddenField(fieldName, index + 1));

        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getHiddenFields();

    /**
     * Builds and returns all worker maintenance form sections.
     *
     * @private
     *
     * @returns The form sections sorted by display order.
     */
    private getFormSections(): FormSection<MantenimientoModel>[] {
        const sections: FormSection<MantenimientoModel>[] = [
            {
                name: 'Datos del trabajador',
                fields: this.getWorkerFields(),
                width: 12,
                isVisible: true,
                order: 1,
            },
            {
                name: 'Carnets',
                fields: this.getCarnetFields(),
                width: 12,
                isVisible: true,
                order: 2,
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 3,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getFormSections();

    /**
     * Builds the root form metadata object.
     *
     * @private
     *
     * @param table - Optional table configuration used by table-based form modes.
     *
     * @returns The root form metadata object.
     */
    private buildForm(table: (string[] | string[][])[] | null): Form<MantenimientoModel> {
        return {
            name: 'mantenimiento',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table: table === null
                ? null
                : { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: { isEnabled: false, label: '' },
            uploadButtons: this.getUploadButtons(),
            buttons: [],
            signed: this.showTrabajadoresFirma,
            signatureConfiguration: {
                folder: '_trabajadores',
                field: 'signature_img',
                uploadStrategy: 'deferredBase64',
                existingImageConfiguration: {
                    idField: 'id',
                    pathPrefix: 'firmas_trabajadores/',
                    extension: '.png',
                },
            },
            payloadTransform: (payload) => this.transformPayload(payload),
        };
    }// buildForm();

    /**
     * Builds and returns the reactive form header upload buttons.
     *
     * File upload controls are rendered by `app-reactive-form-builder`, so the
     * maintenance form no longer needs hidden file inputs or external upload buttons.
     * Existing attachments remain visible in informative and deletion modes through
     * the same upload component view action.
     *
     * @private
     *
     * @returns Upload buttons available for the current worker maintenance form.
     */
    private getUploadButtons(): FormUploadButton[] {
        const uploadButtons: FormUploadButton[] = [];

        if (this.showFotoTrajador && (this.isEditable() || this.hasAttachedFile('adjunto'))) {
            uploadButtons.push({
                isEnabled: true,
                label: 'Añadir foto',
                imageName: 'adjunto',
                imageFolder: 'fotos_trabajadores',
                imageDisplay: 'header',
                accept: 'image/*',
                allowedExtensions: ['gif', 'jpeg', 'jpg', 'jiff', 'png', 'svg', 'tiff'],
                invalidFormatMessage: 'Formato de imagen no válido.',
                uploadUrl: 'ws/trabajadores/upload_file_trabajadores.php',
                viewFolder: 'fotos_trabajadores',
                showPreview: false,
                idField: 'id',
            });
        }// if();

        return uploadButtons;
    }// getUploadButtons();

    /**
     * Indicates whether the current worker model has a stored attachment.
     *
     * @private
     *
     * @param fieldName - Attachment field to inspect.
     *
     * @returns True when the attachment field contains a usable file name.
     */
    private hasAttachedFile(fieldName: 'adjunto'): boolean {
        return String(this.model[fieldName] ?? '').trim().length > 0;
    }// hasAttachedFile();

    /**
     * Creates field conditions using the project defaults.
     *
     * @private
     *
     * @param overrides - Partial condition values to override defaults.
     *
     * @returns A complete field conditions object.
     */
    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }// conditions();

    /**
     * Builds a text-like field.
     *
     * @private
     *
     * @param name - Backend field name.
     * @param label - User-facing label.
     * @param order - Display order.
     * @param isVisible - Visibility flag.
     * @param width - Bootstrap column width.
     * @param conditions - Optional condition overrides.
     * @param inputType - Input type used by the builder.
     *
     * @returns A configured form field.
     */
    private textField(
        name: string,
        label: string,
        order: number,
        isVisible = true,
        width = 3,
        conditions: Partial<FormFieldConditions> = {},
        inputType: InputType = InputType.TEXT,
    ): FormField<MantenimientoModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: {
                type: inputType,
                isEditable: this.isEditable(),
                ...(inputType === InputType.TEXTAREA
                    ? { textAreaConfiguration: { rows: 8, hasVoiceTranscription: false, canTakePhoto: false, photoField: undefined } }
                    : {}),
            },
            width,
            conditions: this.conditions({ isVisible, ...conditions }),
            order,
        };
    }// textField();

    /**
     * Builds a password field.
     *
     * @private
     *
     * @param name - Backend field name.
     * @param label - User-facing label.
     * @param order - Display order.
     * @param isVisible - Visibility flag.
     *
     * @returns A configured password form field.
     */
    private passwordField(
        name: string,
        label: string,
        order: number,
        isVisible: boolean,
        conditions: Partial<FormFieldConditions> = {},
    ): FormField<MantenimientoModel> {
        return this.textField(name, label, order, isVisible, 3, conditions, InputType.PASSWORD);
    }// passwordField();

    /**
     * Builds a dropdown field.
     *
     * @private
     *
     * @param name - Backend field name.
     * @param label - User-facing label.
     * @param order - Display order.
     * @param data - Dropdown data source.
     * @param isVisible - Visibility flag.
     * @param usePrimaryKey - Whether dropdown object values must be serialized by id.
     *
     * @returns A configured dropdown form field.
     */
    private dropdownField<TValue>(
        name: string,
        label: string,
        order: number,
        data: DropdownData<TValue>,
        isVisible: boolean,
        usePrimaryKey = true,
    ): FormField<MantenimientoModel> {
        return {
            name,
            vinculatedFields: undefined,
            label,
            input: {
                type: this.isEditable() ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                isEditable: this.isEditable(),
                data,
                ...(usePrimaryKey ? { dropdownConfiguration: { valuePrimaryKey: 'id' } } : {}),
                optionLabel: 'label',
                optionValue: 'value',
            },
            width: 3,
            conditions: this.conditions({ isVisible }),
            order,
        };
    }// dropdownField();

    /**
     * Builds a hidden field.
     *
     * @private
     *
     * @param name - Hidden control name.
     * @param order - Display order.
     *
     * @returns A configured hidden form field.
     */
    private hiddenField(name: string, order: number): FormField<MantenimientoModel> {
        return {
            name,
            vinculatedFields: undefined,
            label: '',
            input: {
                type: InputType.HIDDEN,
                isEditable: false,
                defaultValue: this.getHiddenFieldDefaultValue(name),
            },
            width: 0,
            conditions: this.conditions(),
            order,
        };
    }// hiddenField();

    /**
     * Appends visible fields to table/export metadata arrays.
     *
     * @private
     *
     * @param fields - Fields to inspect and append when visible.
     *
     * @returns Nothing.
     */
    private appendVisibleFields(fields: FormField<MantenimientoModel>[]): void {
        const visibleFields = fields.filter((field) =>
            field.conditions.isVisible && field.input.type !== InputType.EMPTY && field.input.type !== InputType.HIDDEN,
        );

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));
    }// appendVisibleFields();

    /**
     * Returns the form header based on the active form mode.
     *
     * @private
     *
     * @returns Human-readable form header.
     */
    private getHeader(): string {
        switch (this.formType) {
            case FormType.CREATION:
                return 'Crear ' + environment.features.aplicadorName;
            case FormType.DUPLICATION:
                return 'Duplicar ' + environment.features.aplicadorName;
            case FormType.EDITION:
                return 'Actualizar ' + environment.features.aplicadorName;
            default:
                return 'Datos del trabajador';
        }
    }// getHeader();

    /**
     * Indicates whether normal fields can be edited in the current form mode.
     *
     * @private
     *
     * @returns True for creation, edition and duplication modes.
     */
    private isEditable(): boolean {
        return this.formType < FormType.DELETION;
    }// isEditable();

    /**
     * Returns the worker clock-in password field name depending on the application.
     *
     * @private
     *
     * @returns Backend field name.
     */
    private getPasswordFichajeFieldName(): string {
        return this.appName === 'covidai' ? 'password_fichaje' : 'password';
    }// getPasswordFichajeFieldName();

    /**
     * Indicates whether the clock-in password field is visible.
     *
     * @private
     *
     * @returns True when the current application uses this field.
     */
    private isPasswordFichajeVisible(): boolean {
        return this.appName === 'covidai' || this.appName === 'gfcartera';
    }// isPasswordFichajeVisible();

    /**
     * Filters machinery dropdown values according to ownership and application rules.
     *
     * @private
     *
     * @param value - Dropdown option value to evaluate.
     *
     * @returns True when the option must remain visible.
     */
    private filterMachineryOption(value: unknown): boolean {
        const maquinaria = value as MaquinariaModel | null | undefined;

        if (maquinaria === null || maquinaria === undefined) {
            return true;
        }// if();

        if (this.appName === 'abonosfolques') {
            return true;
        }// if();

        if (maquinaria.activo?.toString() !== '1') {
            return false;
        }// if();

        const hasCommerceFiltering = environment.features.showVentas || this.appName === 'covidai' || this.appName === 'sirga';

        if (hasCommerceFiltering && this.lists.comercios.selected?.id) {
            return this.lists.comercios.selected.id === maquinaria.id_comercio;
        }// if();

        if (StorageManager.getClient() && this.hasClients && this.filterAplicadoresByClient) {
            return maquinaria.id_cliente === StorageManager.getClient()?.id;
        }// if();

        return true;
    }// filterMachineryOption();

    /**
     * Filters supplier dropdown values according to the active application mode.
     *
     * @private
     *
     * @param value - Dropdown option value to evaluate.
     *
     * @returns True when the option must remain visible.
     */
    private filterCommerceOption(value: unknown): boolean {
        const commerce = value as Compradores | null | undefined;

        if (commerce === null || commerce === undefined) {
            return true;
        }// if();

        if (this.appName === 'sirga') {
            return commerce.tipo === 'Proveedor';
        }// if();

        if (this.activateREA) {
            return commerce.tipo === 'Servicios Agrícolas';
        }// if();

        return false;
    }// filterCommerceOption();

    /**
     * Returns the switch label used by applications that rename active ownership.
     *
     * @private
     *
     * @returns Active field label.
     */
    private getActiveLabel(): string {
        if (this.titleActivo !== 'Propio') {
            return this.titleActivo;
        }// if();

        return this.normalizeSwitchValue(this.model.activo) === 1 ? 'Propio' : 'Externo';
    }// getActiveLabel();

    /**
     * Returns the number of rows available for carnet repeatable fields.
     *
     * @private
     *
     * @returns The maximum number of carnet rows.
     */
    private getCarnetRowsLimit(): number {
        return this.appName === 'avafitogest' ? AVAFITOGEST_ROWS : DEFAULT_ROWS;
    }// getCarnetRowsLimit();

    /**
     * Returns the repeatable-group bindings used to serialize carnet rows.
     *
     * @private
     *
     * @returns Logical-to-hidden-field bindings.
     */
    private getRepeatableBindings(): Record<string, string> {
        return {
            tipoCarnet: 'tipo_carnet',
            ropo: 'ropo',
            fechaExp: 'fecha_exp',
            fechaCad: 'fecha_cad',
        };
    }// getRepeatableBindings();

    /**
     * Returns the repeatable-group subfields used by worker carnet rows.
     *
     * @private
     *
     * @returns Repeatable group subfields.
     */
    private getRepeatableSubFields(): RepeatableSubField[] {
        return [
            {
                key: 'ropo',
                label: 'ROPO',
                input: { type: InputType.TEXT, isEditable: this.isEditable() },
            },
            {
                key: 'tipoCarnet',
                label: 'Tipo Carnet',
                input: {
                    type: this.isEditable() ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    isEditable: this.isEditable(),
                    data: this.getTipoCarnetList(),
                    optionLabel: 'label',
                    optionValue: 'value',
                },
            },
            {
                key: 'fechaExp',
                label: 'Fecha Expedición',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: this.isEditable(),
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
            },
            {
                key: 'fechaCad',
                label: 'Fecha Caducidad',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: this.isEditable(),
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
            },
        ];
    }// getRepeatableSubFields();

    /**
     * Returns the carnet-type dropdown list according to the active app.
     *
     * @private
     *
     * @returns Dropdown data for card types.
     */
    private getTipoCarnetList(): DropdownData<string> {
        return this.appName === 'pyf' ? this.lists.tipoCarnetPyf : this.lists.tipoCarnet;
    }// getTipoCarnetList();

    /**
     * Normalizes fields that are sensitive to backend display formats.
     *
     * @private
     *
     * @returns Nothing.
     */
    private normalizeModel(): void {
        this.model.activo = this.normalizeSwitchValue(this.model.activo);

        if (this.model.id_comercio === undefined) {
            this.model.id_comercio = null;
        }// if();
    }// normalizeModel();


    /**
     * Converts backend switch variants to the numeric values expected by PrimeNG.
     *
     * @private
     *
     * @param value - Raw switch value received from the form or backend.
     *
     * @returns `1` when enabled, otherwise `0`.
     */
    private normalizeSwitchValue(value: unknown): 1 | 0 {
        if (value === 1 || value === true) {
            return 1;
        }// if();

        if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase();

            return ['1', 'true', 'si', 'sí', 's', 'yes'].includes(normalizedValue) ? 1 : 0;
        }// if();

        return 0;
    }// normalizeSwitchValue();

    /**
     * Removes view-only signature data from the worker payload.
     *
     * The signature is uploaded after saving because the legacy worker endpoint stores it
     * as `firmas_trabajadores/{id}.png` instead of persisting the base64 value in the table.
     *
     * @private
     *
     * @param payload - Raw form payload generated by the reactive form builder.
     *
     * @returns Worker payload ready for the generic backend endpoint.
     */
    private transformPayload(payload: Record<string, unknown>): Record<string, unknown> {
        const transformedPayload = { ...payload };

        delete transformedPayload['signature_img'];
        delete transformedPayload['has_signature'];

        return transformedPayload;
    }// transformPayload();

    /**
     * Reads a scalar hidden field default value from the current model.
     *
     * @private
     *
     * @param name - Hidden field name.
     *
     * @returns The default value for the hidden field.
     */
    private getHiddenFieldDefaultValue(name: string): string | number | boolean | null | undefined {
        const value = (this.model as Record<string, unknown>)[name];

        if (value === null || value === undefined) {
            return null;
        }// if();

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return value;
        }// if();

        return String(value);
    }// getHiddenFieldDefaultValue();
}// class;
